import React, { useState, useEffect } from 'react';
import { getDailyWorkUpdates, getEmployeeTimeEntries } from '../../services/adminService';
import Preloader from '../../components/common/Preloader';
import { X } from 'lucide-react';
import './AdminDashboard.css'; // Reusing some basic styling if needed
import './AdminWorkUpdates.css';

const AdminWorkUpdates = () => {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Modal State
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeHistory, setEmployeeHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [customDate, setCustomDate] = useState('');

  useEffect(() => {
    fetchUpdates(date);
  }, [date]);

  const fetchUpdates = async (selectedDate) => {
    try {
      setLoading(true);
      const data = await getDailyWorkUpdates(selectedDate);
      setUpdates(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch work updates');
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeClick = async (emp) => {
    setSelectedEmployee(emp);
    setFilterMonth(new Date(date).getMonth() + 1);
    setFilterYear(new Date(date).getFullYear());
  };

  const fetchEmployeeHistory = React.useCallback(async () => {
    try {
      setLoadingHistory(true);
      const data = await getEmployeeTimeEntries(selectedEmployee._id, filterMonth, filterYear);
      const sortedData = data.sort((a, b) => new Date(b.date) - new Date(a.date));
      setEmployeeHistory(sortedData);
    } catch (err) {
      alert('Failed to load history: ' + err.message);
    } finally {
      setLoadingHistory(false);
    }
  }, [selectedEmployee, filterMonth, filterYear]);

  useEffect(() => {
    if (selectedEmployee) {
      fetchEmployeeHistory();
    }
  }, [selectedEmployee, fetchEmployeeHistory]);

  return (
    <div className="admin-work-updates-page fade-in">
      <div className="admin-work-updates-header">
        <h1>Daily Work Updates</h1>
        <div className="admin-work-updates-controls">
          <input 
            type="text" 
            placeholder="Search employee..." 
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)} 
            className="admin-work-search-input"
          />
          <div className="admin-work-date-group">
            <label>Select Date:</label>
            <input 
              type="date" 
              value={date} 
              onChange={(e) => setDate(e.target.value)} 
              className="admin-work-date-input"
            />
          </div>
        </div>
      </div>

      {error && <div className="error-message" style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>}

      {loading ? (
        <Preloader />
      ) : (
        <div className="admin-work-card">
          {updates.filter(emp => emp.name.toLowerCase().includes(searchQuery.toLowerCase()) || (emp.employeeId && emp.employeeId.toLowerCase().includes(searchQuery.toLowerCase()))).length === 0 ? (
            <p>No employees found.</p>
          ) : (
            <div className="admin-work-list">
              {updates.filter(emp => emp.name.toLowerCase().includes(searchQuery.toLowerCase()) || (emp.employeeId && emp.employeeId.toLowerCase().includes(searchQuery.toLowerCase()))).map(emp => (
                <div 
                  key={emp._id} 
                  className="admin-work-item"
                  onClick={() => handleEmployeeClick(emp)}
                >
                  <div className="admin-work-emp-col">
                    <div className="admin-work-emp-header">
                      {emp.profilePhoto ? (
                        <img src={emp.profilePhoto} alt={emp.name} className="admin-work-avatar" />
                      ) : (
                        <div className="admin-work-avatar-placeholder">
                          {emp.name.charAt(0)}
                        </div>
                      )}
                      <div>
                        <div className="admin-work-emp-name">{emp.name}</div>
                        <div className="admin-work-emp-id">{emp.employeeId}</div>
                      </div>
                    </div>
                    <div className="admin-work-emp-designation">{emp.designation}</div>
                    <div className="admin-work-view-link">Click to view all updates</div>
                  </div>
                  <div className="admin-work-content-col">
                    <div className="admin-work-content-label">Work Update:</div>
                    {emp.workUpdate ? (
                      <div className="admin-work-content-text">
                        {emp.workUpdate}
                      </div>
                    ) : (
                      <div className="admin-work-empty-text">
                        No work update submitted for this date.
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Employee History Modal */}
      {selectedEmployee && (
        <div 
          className="admin-work-modal-overlay" 
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelectedEmployee(null);
          }}
        >
          <div className="admin-work-modal-container">
            <div className="admin-work-modal-header">
              <h2 className="admin-work-modal-title">Work Updates: {selectedEmployee.name}</h2>
              <button 
                type="button"
                className="admin-work-modal-close-btn"
                onClick={() => setSelectedEmployee(null)}
                aria-label="Close"
                title="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="admin-work-modal-body">
              {/* Left Side: Filters */}
              <div className="admin-work-modal-sidebar">
                <h3 style={{ marginTop: 0, marginBottom: '1.5rem', fontSize: '1.1rem', color: '#374151' }}>Filter Updates</h3>
                
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold', fontSize: '0.9rem', color: '#4b5563' }}>Month:</label>
                  <select value={filterMonth} onChange={(e) => { setFilterMonth(parseInt(e.target.value)); setCustomDate(''); }} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                      <option key={m} value={m}>{new Date(2000, m - 1).toLocaleString('default', { month: 'long' })}</option>
                    ))}
                  </select>
                </div>
                
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold', fontSize: '0.9rem', color: '#4b5563' }}>Year:</label>
                  <select value={filterYear} onChange={(e) => { setFilterYear(parseInt(e.target.value)); setCustomDate(''); }} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}>
                    {[...Array(5)].map((_, i) => {
                      const year = new Date().getFullYear() - i;
                      return <option key={year} value={year}>{year}</option>;
                    })}
                  </select>
                </div>

                <hr style={{ margin: '1.5rem 0', borderColor: '#e5e7eb' }} />
                
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold', fontSize: '0.9rem', color: '#4b5563' }}>Custom Date:</label>
                  <input 
                    type="date" 
                    value={customDate} 
                    onChange={(e) => {
                      setCustomDate(e.target.value);
                      if (e.target.value) {
                        const d = new Date(e.target.value);
                        setFilterMonth(d.getMonth() + 1);
                        setFilterYear(d.getFullYear());
                      }
                    }} 
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                  />
                  {customDate && (
                    <button 
                      onClick={() => setCustomDate('')}
                      style={{ marginTop: '0.75rem', width: '100%', padding: '0.5rem', backgroundColor: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 'bold' }}
                    >
                      Clear Custom Date
                    </button>
                  )}
                </div>

                <hr style={{ margin: '1.5rem 0', borderColor: '#e5e7eb' }} />

                {/* Date List */}
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.95rem', color: '#4b5563' }}>Available Dates:</h4>
                  {employeeHistory.filter(e => e.workUpdate).length === 0 ? (
                    <div style={{ fontSize: '0.85rem', color: '#9ca3af', fontStyle: 'italic' }}>No updates available.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '35vh', overflowY: 'auto', paddingRight: '0.25rem' }}>
                      <button 
                        onClick={() => setCustomDate('')}
                        style={{ 
                          padding: '0.5rem', textAlign: 'left', borderRadius: '4px', border: '1px solid',
                          borderColor: !customDate ? '#6366f1' : '#e5e7eb',
                          backgroundColor: !customDate ? '#eef2ff' : 'white',
                          color: !customDate ? '#4f46e5' : '#374151',
                          cursor: 'pointer', fontWeight: !customDate ? 'bold' : 'normal',
                          fontSize: '0.85rem'
                        }}
                      >
                        All Dates in Month
                      </button>
                      {Array.from(new Set(employeeHistory.filter(e => e.workUpdate).map(e => new Date(e.date).toISOString().split('T')[0]))).sort((a, b) => new Date(b) - new Date(a)).map(d => {
                        const dateObj = new Date(d);
                        const isSelected = customDate && new Date(customDate).toISOString().split('T')[0] === d;
                        return (
                          <button 
                            key={d}
                            onClick={() => setCustomDate(d)}
                            style={{ 
                              padding: '0.5rem', textAlign: 'left', borderRadius: '4px', border: '1px solid',
                              borderColor: isSelected ? '#6366f1' : '#e5e7eb',
                              backgroundColor: isSelected ? '#eef2ff' : 'white',
                              color: isSelected ? '#4f46e5' : '#374151',
                              cursor: 'pointer', fontWeight: isSelected ? 'bold' : 'normal',
                              fontSize: '0.85rem'
                            }}
                          >
                            {dateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Right Side: Updates List */}
              <div className="admin-work-modal-content">
                {loadingHistory ? (
                  <Preloader />
                ) : employeeHistory.filter(e => {
                    if (!e.workUpdate) return false;
                    if (customDate) {
                      const entryDate = new Date(e.date);
                      const selectedD = new Date(customDate);
                      return entryDate.getDate() === selectedD.getDate() && 
                             entryDate.getMonth() === selectedD.getMonth() && 
                             entryDate.getFullYear() === selectedD.getFullYear();
                    }
                    return true;
                  }).length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem 1rem', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px dashed #d1d5db' }}>
                    <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>📅</div>
                    <h3 style={{ color: '#374151', margin: '0 0 0.5rem 0' }}>No Work Updates Found</h3>
                    <p style={{ color: '#6b7280', margin: 0 }}>
                      {customDate ? `No updates available for ${new Date(customDate).toLocaleDateString()}.` : 'No updates available for the selected month and year.'}
                    </p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {employeeHistory.filter(e => {
                        if (!e.workUpdate) return false;
                        if (customDate) {
                          const entryDate = new Date(e.date);
                          const selectedD = new Date(customDate);
                          return entryDate.getDate() === selectedD.getDate() && 
                                 entryDate.getMonth() === selectedD.getMonth() && 
                                 entryDate.getFullYear() === selectedD.getFullYear();
                        }
                        return true;
                      }).map(entry => {
                      const d = new Date(entry.updatedAt || entry.date);
                      const dd = String(d.getDate()).padStart(2, '0');
                      const mm = String(d.getMonth() + 1).padStart(2, '0');
                      const yyyy = d.getFullYear();
                      
                      let hours = d.getHours();
                      const minutes = String(d.getMinutes()).padStart(2, '0');
                      const seconds = String(d.getSeconds()).padStart(2, '0');
                      const ampm = hours >= 12 ? 'PM' : 'AM';
                      hours = hours % 12;
                      hours = hours ? hours : 12;
                      const hh = String(hours).padStart(2, '0');
                      
                      const titleStr = `Daily Work Update (Date: ${dd}-${mm}-${yyyy}, Time: ${hh}:${minutes}:${seconds} ${ampm} IST)`;

                      return (
                        <div key={entry._id} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1.5rem', backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                          <div style={{ fontWeight: 'bold', marginBottom: '1rem', color: '#4f46e5', borderBottom: '1px solid #f3f4f6', paddingBottom: '0.75rem', fontSize: '1.05rem' }}>
                            {titleStr}
                          </div>
                          <div style={{ whiteSpace: 'pre-wrap', color: '#1f2937', lineHeight: '1.6' }}>
                            {entry.workUpdate}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Bottom Close Action */}
            <div className="admin-work-modal-footer">
              <button 
                type="button" 
                className="admin-work-modal-footer-close-btn" 
                onClick={() => setSelectedEmployee(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWorkUpdates;
